/**
 * Helper d'invalidation centralisé pour les DataTables serveur.
 *
 * Trois queryKeys distincts cohabitent en mode serveur :
 *   1. Factory module ad-hoc (ex: `queryKeys.learners.all`) — utilisé par
 *      les hooks classiques (`useLearnerDetail`, etc.).
 *   2. `[endpoint]` — listing paginé courant (`useDataTableServer`).
 *   3. `['datatable-groups', endpoint]` — compteurs en mode regroupé
 *      (`useGrouping`).
 *
 * Sans la 3e invalidation, le mode regroupé reste figé après mutation
 * (bug constaté pré-prod 2026-05-08 sur learners). À utiliser dans tous
 * les `onSuccess` des mutations qui modifient une ressource listée par
 * une DataTable.
 *
 * `refetchType: 'all'` est requis par la doctrine datatable-audit-expert §E
 * pour refetcher même les queries inactives (la table peut être démontée
 * pendant qu'on est sur une fiche détail).
 */

import type { QueryClient } from '@tanstack/react-query'

/**
 * Rend une promesse résolue quand les refetchs sont terminés. L'ignorer reste
 * parfaitement valide (c'est le cas de la grande majorité des appelants) : on
 * l'attend uniquement là où le geste doit sembler FINI avant que la main soit
 * rendue — une suppression dont le toast de succès ne doit pas précéder la
 * disparition de la ligne.
 */
export function invalidateDataTableListings(
    qc: QueryClient,
    listingEndpoint: string,
    factoryQueryKey?: readonly unknown[],
): Promise<void> {
    const pending: Promise<void>[] = []
    if (factoryQueryKey) {
        pending.push(qc.invalidateQueries({ queryKey: factoryQueryKey, refetchType: 'all' }))
    }
    pending.push(qc.invalidateQueries({ queryKey: [listingEndpoint], refetchType: 'all' }))
    pending.push(
        qc.invalidateQueries({
            queryKey: ['datatable-groups', listingEndpoint],
            refetchType: 'all',
        }),
    )
    // Compteurs facettés du panneau de filtres (['datatable-facets', facetsEndpoint,
    // params]) : le facetsEndpoint diffère du listing (souvent `<listing>/facets` mais
    // pas garanti) → invalidation par PRÉFIXE global. Sans elle, les compteurs (ex.
    // État ACTIVE/PAUSED) restent figés 30 s après la mutation qui les change.
    pending.push(qc.invalidateQueries({ queryKey: ['datatable-facets'], refetchType: 'all' }))
    // Barre d'agrégats du bas (['datatable-aggregations', aggregationEndpoint, params],
    // cf. `aggregations/StatusBar.tsx`) : elle n'était invalidée QUE par l'édition en
    // ligne d'une cellule, jamais par une mutation extérieure à la table. Or c'est
    // exactement la ligne qu'on regarde pour juger de l'effet d'un réglage — elle
    // continuait d'annoncer l'ancienne moyenne au-dessous d'une table déjà rechargée.
    // Par PRÉFIXE, comme les facettes : l'endpoint d'agrégats n'est pas déductible du
    // listing (souvent `<listing>/aggregations`, jamais garanti).
    pending.push(qc.invalidateQueries({ queryKey: ['datatable-aggregations'], refetchType: 'all' }))
    return Promise.all(pending).then(() => undefined)
}
